/*
  <lifestyle-type-rows>
  Lazy product rows for a lifestyle collection (sections/lifestyle-type-rows.liquid).

  Every row is server-rendered empty with a loader and a URL for its products:
  the collection filtered to one product type, rendered through
  sections/helper-lifestyle-type-row.liquid. A row fetches when it comes within
  ROOT_MARGIN of the screen, a few rows at a time, so a 95-type collection
  never fires 95 requests on load.

  The quick type chips (sections/lifestyle-type-filter.liquid):
    - get a sticky copy pinned under the site's sticky header once the shopper
      scrolls past the original, until the last row has gone by;
    - highlight the type whose row is at the top of the screen, and slide that
      chip into view;
    - jump to a row and load it straight away rather than waiting for the
      observer.

  Inserted cards are plain theme markup: the quick-buy custom elements upgrade
  on insertion, so the + button works like everywhere else. Type banners
  (assets/component-type-banners.js) sit above a row's .product-type-grid, not
  inside it, so filling the grid later leaves them in place.
*/
(function () {
  var ROOT_MARGIN = '600px 0px';

  /*
    With a filter applied the page shows the normal grid
    (main-collection-product-grid, "only when filtered"). Clearing every filter
    through the facets script re-renders that section without a reload, and in
    rows mode it renders only a [data-lifestyle-reload] marker — reload then so
    the rows come back.
  */
  function watchForReloadMarker() {
    if (window.__ltsReloadWatcher) return;
    window.__ltsReloadWatcher = true;
    // Only markers inserted after load count: in rows mode the page's own HTML
    // already contains one, and reacting to that would reload forever.
    var observer = new MutationObserver(function (mutations) {
      var found = mutations.some(function (mutation) {
        return Array.prototype.some.call(mutation.addedNodes, function (node) {
          return node.nodeType === 1 && (node.matches('[data-lifestyle-reload]') || node.querySelector('[data-lifestyle-reload]'));
        });
      });
      if (found) {
        observer.disconnect();
        window.location.href = window.location.pathname;
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /* Height of the theme's sticky header while it is showing, else 0. It slides
     in on scroll up and out on scroll down (assets/section-header.js). */
  function siteStickyHeight() {
    var sticky = document.querySelector('.sticky-header');
    return sticky && sticky.classList.contains('show') ? sticky.offsetHeight : 0;
  }

  /* Arrows for a chip line: shown only when it can scroll that way. */
  function enhanceChipsBar(bar) {
    var nav = bar.querySelector('[data-lts-chips]');
    var prev = bar.querySelector('[data-lts-scroll="-1"]');
    var next = bar.querySelector('[data-lts-scroll="1"]');
    if (!nav) return;

    var update = function () {
      var max = nav.scrollWidth - nav.clientWidth;
      if (prev) prev.hidden = nav.scrollLeft <= 4;
      if (next) next.hidden = nav.scrollLeft >= max - 4;
    };

    [prev, next].forEach(function (button) {
      if (!button) return;
      button.addEventListener('click', function () {
        nav.scrollBy({ left: parseInt(button.dataset.ltsScroll, 10) * nav.clientWidth * 0.7, behavior: 'smooth' });
      });
    });

    nav.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  if (typeof customElements.get('lifestyle-type-rows') === 'undefined') {

    class LifestyleTypeRows extends HTMLElement {

      connectedCallback() {
        this.concurrency = Math.max(1, parseInt(this.dataset.concurrency, 10) || 3);
        this.queue = [];
        this.active = 0;
        this.rows = Array.prototype.slice.call(this.querySelectorAll('[data-lts-row]'));

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(this.onIntersect.bind(this), { rootMargin: ROOT_MARGIN });
          this.rows.forEach(function (row) { this.observer.observe(row); }, this);
        } else {
          this.rows.forEach(function (row) { this.enqueue(row); }, this);
        }

        this.setupChips();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        if (this.stickyBar) this.stickyBar.remove();
      }

      /* ------------------------------------------------------------ loading */

      onIntersect(entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          this.observer.unobserve(entry.target);
          this.enqueue(entry.target);
        }, this);
      }

      enqueue(row, urgent) {
        if (row.dataset.ltsState) {
          if (urgent && row.dataset.ltsState === 'queued') {
            this.queue.splice(this.queue.indexOf(row), 1);
            this.queue.unshift(row);
          }
          return;
        }
        row.dataset.ltsState = 'queued';
        if (urgent) this.queue.unshift(row); else this.queue.push(row);
        this.pump();
      }

      pump() {
        while (this.active < this.concurrency && this.queue.length) {
          this.load(this.queue.shift());
        }
      }

      load(row) {
        var self = this;
        var rail = row.querySelector('[data-lts-products]');
        row.dataset.ltsState = 'loading';
        this.active += 1;

        fetch(row.dataset.ltsUrl)
          .then(function (res) {
            if (!res.ok) throw new Error('row ' + res.status);
            return res.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var products = doc.querySelector('[data-lts-row-products]');
            if (!products || !products.children.length) {
              row.hidden = true; // e.g. every product of the type was unpublished since
              return;
            }
            rail.innerHTML = products.innerHTML;
            rail.removeAttribute('aria-busy');
            row.dataset.ltsState = 'done';
            // Loaders take the real row height from now on, so rows above a
            // chip's target don't push it down as they fill in.
            if (!self.railHeight && rail.offsetHeight) {
              self.railHeight = rail.offsetHeight;
              self.style.setProperty('--lts-rail-height', self.railHeight + 'px');
            }
            if (self.jumpTarget === row) self.alignTo(row);
          })
          .catch(function (e) {
            console.warn('[lifestyle-types]', e);
            row.dataset.ltsState = '';
            self.showError(row, rail);
          })
          .then(function () {
            self.active -= 1;
            self.pump();
          });
      }

      showError(row, rail) {
        var self = this;
        rail.removeAttribute('aria-busy');
        rail.innerHTML = '';
        var box = document.createElement('div');
        box.className = 'lts-error';
        var text = document.createElement('span');
        text.textContent = this.dataset.errorLabel || "Couldn't load these products.";
        var retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'lts-error__retry';
        retry.textContent = this.dataset.retryLabel || 'Try again';
        retry.addEventListener('click', function () {
          rail.innerHTML = '<div class="lts-loader" role="status"><span class="lts-loader__spinner" aria-hidden="true"></span></div>';
          rail.setAttribute('aria-busy', 'true');
          self.enqueue(row, true);
        });
        box.appendChild(text);
        box.appendChild(retry);
        rail.appendChild(box);
      }

      /* -------------------------------------------------------------- chips */

      setupChips() {
        var self = this;
        this.chipsBar = document.querySelector('.lts-filter [data-lts-chips-bar]');

        if (this.chipsBar) {
          enhanceChipsBar(this.chipsBar);

          // The sticky copy lives on <body>, like the theme's own sticky header.
          this.stickyBar = document.createElement('div');
          this.stickyBar.className = 'lts-sticky-chips';
          this.stickyBar.setAttribute('aria-hidden', 'true');
          var inner = document.createElement('div');
          inner.className = 'container--large';
          inner.appendChild(this.chipsBar.cloneNode(true));
          this.stickyBar.appendChild(inner);
          document.body.appendChild(this.stickyBar);
          enhanceChipsBar(this.stickyBar);
        }

        // One listener for both chip lines.
        document.addEventListener('click', function (event) {
          var chip = event.target.closest && event.target.closest('[data-lts-chip]');
          if (!chip) return;
          var row = document.getElementById(chip.dataset.ltsChip);
          if (!row) return;
          event.preventDefault();
          self.jumpTo(row);
        });

        var ticking = false;
        this.onScroll = function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            ticking = false;
            self.updateStickyBar();
            self.updateActiveChip();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);

        // The site header shows/hides on its own schedule; follow it.
        var siteSticky = document.querySelector('.sticky-header');
        if (siteSticky) {
          new MutationObserver(this.onScroll).observe(siteSticky, { attributes: true, attributeFilter: ['class'] });
        }

        this.onScroll();
      }

      /* Visible once the original chips have scrolled away, until the rows end. */
      updateStickyBar() {
        if (!this.stickyBar || !this.chipsBar) return;
        var top = siteStickyHeight();
        this.stickyBar.style.setProperty('--lts-sticky-top', top + 'px');

        var pastChips = this.chipsBar.getBoundingClientRect().bottom < top;
        var rowsLeft = this.getBoundingClientRect().bottom > top + this.stickyBar.offsetHeight + 40;
        var visible = pastChips && rowsLeft;

        if (visible !== this.stickyVisible) {
          this.stickyVisible = visible;
          this.stickyBar.classList.toggle('is-visible', visible);
          this.stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
          if (visible && this.activeId) this.revealChip(this.stickyBar, this.activeId, false);
        }
      }

      /* Everything pinned at the top of the screen right now. */
      topOffset() {
        var bar = this.stickyVisible && this.stickyBar ? this.stickyBar.offsetHeight : 0;
        return siteStickyHeight() + bar;
      }

      /* The active type is the last row whose top has reached the pinned bars. */
      updateActiveChip() {
        var line = this.topOffset() + 60;
        var current = null;
        for (var i = 0; i < this.rows.length; i++) {
          var row = this.rows[i];
          if (row.hidden) continue;
          if (row.getBoundingClientRect().top <= line) current = row; else break;
        }
        if (current && this.getBoundingClientRect().bottom < line) current = null;
        this.setActive(current ? current.id : null);
      }

      setActive(id) {
        if (id === this.activeId) return;
        this.activeId = id;
        document.querySelectorAll('[data-lts-chip]').forEach(function (chip) {
          var on = chip.dataset.ltsChip === id;
          chip.classList.toggle('is-active', on);
          if (on) chip.setAttribute('aria-current', 'true'); else chip.removeAttribute('aria-current');
        });
        if (id && this.stickyBar) this.revealChip(this.stickyBar, id, true);
      }

      /* Scroll a chip line sideways so the chip is centred — never the page. */
      revealChip(scope, id, smooth) {
        var nav = scope.querySelector('[data-lts-chips]');
        var chip = nav && nav.querySelector('[data-lts-chip="' + id + '"]');
        if (!chip) return;
        var left = chip.offsetLeft - (nav.clientWidth - chip.offsetWidth) / 2;
        nav.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
      }

      jumpTo(row) {
        var self = this;
        if (this.observer) this.observer.unobserve(row);
        this.enqueue(row, true);
        // An instant jump: a smooth scroll would pass every row on the way
        // and queue all of them for loading.
        this.jumpTarget = row;
        this.alignTo(row);
        this.setActive(row.id);
        clearTimeout(this.jumpTimer);
        this.jumpTimer = setTimeout(function () {
          self.alignTo(row);
          self.jumpTarget = null;
        }, 2500);
        if (history.replaceState) history.replaceState(null, '', '#' + row.id);
      }

      alignTo(row) {
        var rowTop = row.getBoundingClientRect().top + window.scrollY;
        // Leave room for the site header even when it is hidden right now: any
        // small scroll up (including the re-align below) slides it back in and
        // pushes the chip bar down over the row heading.
        var siteSticky = document.querySelector('.sticky-header');
        var header = siteSticky ? siteSticky.offsetHeight : 0;
        var bar = this.stickyBar ? (this.stickyBar.offsetHeight || 54) : 0;
        var offset = header + bar + 12;
        window.scrollTo({ top: Math.max(0, rowTop - offset), behavior: 'auto' });
      }
    }

    customElements.define('lifestyle-type-rows', LifestyleTypeRows);
  }

  watchForReloadMarker();
})();
